import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/** View-model van één poort-blokje op de geheugencircuit cheat sheet. */
export interface MemoryCheatBlockVm {
  readonly gate: number;
  readonly color: string;
  readonly keynote: string;
  readonly description: string;
  readonly iching: string;
  readonly gateOf: string;
}

/**
 * Eén poort-blokje van de Geheugencircuit cheat sheet (Figma node 1410-1596):
 * poortnummer + memory-keynote bovenaan, de memory-omschrijving eronder, en
 * onderaan de bestaande I Ching-omschrijving + de twee algemene poort-keynotes.
 * Puur presentatie; de pagina bouwt het {@link MemoryCheatBlockVm} (kleur uit
 * de Figma-pastels, tekst uit de memory- en gates-scopes).
 */
@Component({
  selector: 'hd-memory-cheat-block',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mem-block',
    '[style.background]': 'block().color',
    '[attr.data-gate]': 'block().gate',
  },
  template: `
    <header class="mem-block__head">
      <span class="mem-block__gate">{{ block().gate }}</span>
      <span class="mem-block__keynote">{{ block().keynote }}</span>
    </header>
    @if (block().description) {
      <p class="mem-block__desc">{{ block().description }}</p>
    }
    <footer class="mem-block__footer">
      @if (block().iching) {
        <p class="mem-block__iching">{{ block().iching }}</p>
      }
      @if (block().gateOf) {
        <p class="mem-block__gateof">{{ block().gateOf }}</p>
      }
    </footer>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px 14px 14px;
      border-radius: 10px;
      color: #3d414e;
      text-align: center;
    }

    .mem-block__head {
      display: flex;
      align-items: baseline;
      gap: 10px;
    }

    .mem-block__gate {
      flex: 0 0 auto;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 22px;
      line-height: 1;
    }

    .mem-block__keynote {
      flex: 1 1 auto;
      text-align: right;
      font-weight: 800;
      font-size: 16px;
      line-height: 1.2;
    }

    .mem-block__desc {
      margin: 0;
      font-size: 12.5px;
      line-height: 1.4;
    }

    .mem-block__footer {
      margin-top: auto;
    }

    .mem-block__iching {
      margin: 0;
      font-size: 11px;
      font-weight: 700;
      line-height: 1.35;
    }

    .mem-block__gateof {
      margin: 3px 0 0;
      font-size: 11px;
      font-weight: 700;
      line-height: 1.35;
    }
  `,
})
export class HdMemoryCheatBlockComponent {
  readonly block = input.required<MemoryCheatBlockVm>();
}
